import React from 'react';
import { Plus, Edit, Trash2, Search, MapPin } from 'lucide-react';
import { useCrud, useModal, useForm } from '../hooks';
import { localService } from '../services/api';

const Locais = () => {
  const { data: locais, loading, error, create, update, remove, refresh } = useCrud(localService, 'locais');
  const modal = useModal();
  const [searchTerm, setSearchTerm] = React.useState('');

  const validationRules = {
    nome: { required: true, label: 'Nome', minLength: 2, maxLength: 100 },
    tipo: { required: true, label: 'Tipo' },
    capacidade_total: { 
      required: true, 
      label: 'Capacidade total',
      custom: (value) => {
        const num = parseInt(value);
        if (isNaN(num) || num <= 0) {
          return 'Capacidade deve ser um número maior que 0';
        }
        return null;
      }
    },
    genero_esperado: { required: true, label: 'Gênero esperado' },
    endereco: { required: false, label: 'Endereço', maxLength: 255 }
  };

  const form = useForm(
    { nome: '', tipo: '', capacidade_total: '', genero_esperado: '', endereco: '' },
    validationRules
  );

  const filteredLocais = locais.filter(local =>
    local.nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = () => {
    form.reset();
    modal.open({ mode: 'create', title: 'Novo Local' });
  };

  const handleEdit = (local) => {
    form.reset({
      nome: local.nome || '',
      tipo: local.tipo || '',
      capacidade_total: local.capacidade_total || '',
      genero_esperado: local.genero_esperado || '',
      endereco: local.endereco || ''
    });
    modal.open({ mode: 'edit', title: 'Editar Local', id: local.id });
  };

  const handleDelete = async (local) => {
    if (window.confirm(`Deseja realmente excluir o local "${local.nome}"?`)) {
      const result = await remove(local.id);
      if (result.success) {
        // Success handled by the hook
      }
    }
  };

  const handleSubmit = async (values) => {
    const result = modal.data.mode === 'create' 
      ? await create(values)
      : await update(modal.data.id, values);

    if (result.success) {
      modal.close();
      form.reset();
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Locais</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          <Plus size={20} />
          Novo Local
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar locais..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10"
            />
          </div>
          <div className="text-sm text-gray-500">
            {filteredLocais.length} local(is) encontrado(s)
          </div>
        </div>
      </div>

      <div className="card">
        {filteredLocais.length === 0 ? (
          <div className="empty-state">
            <MapPin className="empty-state-icon" />
            <h3 className="text-lg font-medium mb-2">Nenhum local encontrado</h3>
            <p className="mb-4">
              {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece criando um novo local.'}
            </p>
            {!searchTerm && (
              <button onClick={handleCreate} className="btn btn-primary">
                <Plus size={20} />
                Novo Local
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Capacidade</th>
                  <th>Gênero</th>
                  <th>Endereço</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredLocais.map((local) => (
                  <tr key={local.id}>
                    <td className="font-medium">{local.nome}</td>
                    <td>
                      <span className={`badge ${
                        local.tipo === 'igreja' ? 'badge-success' : 'badge-secondary'
                      }`}>
                        {local.tipo}
                      </span>
                    </td>
                    <td>{local.capacidade_total || '-'}</td>
                    <td>{local.genero_esperado || '-'}</td>
                    <td>{local.endereco || '-'}</td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(local)}
                          className="btn btn-sm btn-secondary"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(local)}
                          className="btn btn-sm btn-danger"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal.isOpen && (
        <div className="modal-overlay" onClick={modal.close}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{modal.data.title}</h2>
              <button onClick={modal.close} className="modal-close">
                ×
              </button>
            </div>

            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="form-group">
                <label className="form-label">Nome *</label>
                <input
                  type="text"
                  value={form.values.nome}
                  onChange={(e) => form.setValue('nome', e.target.value)}
                  onBlur={() => form.setTouched('nome')}
                  className={`form-input ${form.errors.nome && form.touched.nome ? 'border-red-500' : ''}`}
                  placeholder="Digite o nome do local"
                />
                {form.errors.nome && form.touched.nome && (
                  <div className="form-error">{form.errors.nome}</div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Tipo *</label>
                  <select
                    value={form.values.tipo}
                    onChange={(e) => form.setValue('tipo', e.target.value)}
                    onBlur={() => form.setTouched('tipo')}
                    className={`form-select ${form.errors.tipo && form.touched.tipo ? 'border-red-500' : ''}`}
                  >
                    <option value="">Selecione um tipo</option>
                    <option value="igreja">Igreja</option>
                    <option value="privado">Privado</option>
                  </select>
                  {form.errors.tipo && form.touched.tipo && (
                    <div className="form-error">{form.errors.tipo}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Gênero Esperado *</label>
                  <select
                    value={form.values.genero_esperado}
                    onChange={(e) => form.setValue('genero_esperado', e.target.value)}
                    onBlur={() => form.setTouched('genero_esperado')}
                    className={`form-select ${form.errors.genero_esperado && form.touched.genero_esperado ? 'border-red-500' : ''}`}
                  >
                    <option value="">Selecione um gênero</option>
                    <option value="masculino">Masculino</option>
                    <option value="feminino">Feminino</option>
                    <option value="misto">Misto</option>
                  </select>
                  {form.errors.genero_esperado && form.touched.genero_esperado && (
                    <div className="form-error">{form.errors.genero_esperado}</div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Capacidade Total *</label>
                <input
                  type="number"
                  value={form.values.capacidade_total}
                  onChange={(e) => form.setValue('capacidade_total', e.target.value)}
                  onBlur={() => form.setTouched('capacidade_total')}
                  className={`form-input ${form.errors.capacidade_total && form.touched.capacidade_total ? 'border-red-500' : ''}`}
                  placeholder="Capacidade total de pessoas"
                  min="1"
                />
                {form.errors.capacidade_total && form.touched.capacidade_total && (
                  <div className="form-error">{form.errors.capacidade_total}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Endereço</label>
                <textarea
                  value={form.values.endereco}
                  onChange={(e) => form.setValue('endereco', e.target.value)}
                  className="form-input"
                  placeholder="Digite o endereço do local"
                  rows="2"
                />
              </div>

              <div className="modal-footer">
                <button type="button" onClick={modal.close} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {modal.data.mode === 'create' ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Locais;